
   import { defineComponent } from "vue";
   import { OnlineWaiverSign, OnlineWaiverView } from "@/ApiInterfaces";
   import PublicHeader from "@/components/PublicHeader.vue";
   import ModalLoading from "@/components/ModalLoading.vue";
   import ModalErrorMessage from "@/components/ModalErrorMessage.vue";
   import ModalInfoMessage from "@/components/ModalInfoMessage.vue";
   import { parseApiError, fieldError, getFieldErrorMsg, hasFieldError } from "@/apiErrorHandling";

   interface WaiverData {
      waiver: null | OnlineWaiverView;
      signature: OnlineWaiverSign;
      loading: boolean;
      submitted: boolean;
      errorMessage: null | string;
      fieldErrors: null | fieldError[];
   }

   export default defineComponent({
      name: "OnlineWaiver",
      data(): WaiverData {
         return {
            waiver: null,
            signature: { signName: "" },
            loading: true,
            submitted: false,
            errorMessage: null,
            fieldErrors: null
         };
      },
      components: {
         PublicHeader,
         ModalLoading,
         ModalErrorMessage,
         ModalInfoMessage
      },
      directives: {
         focus: {
            mounted(el) {
               el.focus();
            },
         },
      },
      created() {
         document.title = `Online Waiver for Tour # ${this.$route.params.tourId} | Group Trips`;

         window.addEventListener("beforeunload", (e) => {
            if (!this.submitted) {
               e.preventDefault()
               const message = "You have not electronically recorded your agreement to this waiver yet. Are you sure you wish to leave?"
               e.returnValue = message
               return message
            }
         })

         this.fetchWaiver();
      },
      computed: {
         tripDates(): string {
            if (this.waiver) {
               return `${new Date(this.waiver!.startDate.toString()).toLocaleDateString("en-US")} to ${new Date(this.waiver!.returnDate.toString()).toLocaleDateString("en-US")}`;
            } else {
               return "";
            }
         }
      },
      methods: {
         fetchWaiver(): void {
            try {
               fetch(
                  `/api/OnlineWaiver/${this.$route.params.gtUrl}/${this.$route.params.tourId}/${this.$route.params.emailToken}`
               )
                  .then((r) => {
                     if (r.ok) {
                        r.json()
                           .then((json) => {
                              this.waiver = json as OnlineWaiverView;
                              document.title = `Online Waiver for Tour # ${this.waiver.tourId} - ${this.waiver.tourName} - ${this.tripDates} | Group Trips`;
                              this.loading = false;
                              return;
                           })
                           .catch((e) => {
                              console.error(e.toString());
                              window.location.href = '/' + this.$route.params.gtUrl;
                           });
                     } else {
                        // Trip not found so go to the group's home page
                        window.location.href = '/' + this.$route.params.gtUrl;
                     }
                  })
                  .catch((e) => {
                     console.error(e.toString());
                     window.location.href = '/' + this.$route.params.gtUrl;
                  });
            } catch (e) {
               console.error(e.toString());
               window.location.href = '/' + this.$route.params.gtUrl;
            }
         },
         signWaiver(): void {
            try {
               this.loading = true;

               fetch(
                  `/api/OnlineWaiver/${this.$route.params.gtUrl}/${this.$route.params.tourId}/${this.waiver?.waiverReadToken}`,
                  {
                     method: "put",
                     headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                     },
                     body: JSON.stringify(this.signature),
                  }
               )
                  .then((r) => {
                     this.loading = false;
                     if (r.ok) {
                        this.submitted = true;
                     } else {
                        r.text().then((t) => {
                           const errs = parseApiError(t);

                           // Setting these will automatically update the display and show an error
                           this.fieldErrors = errs.fieldErrors;
                           this.errorMessage = errs.message;
                        });
                     }
                  })
                  .catch((e) => {
                     this.loading = false;
                     this.errorMessage = "Something went wrong. Please try again later.";
                     console.error(e.toString());
                  });
            } catch (e) {
               this.loading = false;
               this.errorMessage = "Something went wrong. Please try again later.";
               console.error(e.toString());
            }
         },
         closeSuccess() {
            window.location.href = `/${this.$route.params.gtUrl}/${this.$route.params.tourId}`
         }
      },
   });
